import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { apiService } from '../services/api';
import { ArrowLeft, Plus, Minus, RotateCcw, Check } from 'lucide-react';

const WaiverForm: React.FC = () => {
  const navigate = useNavigate();
  const signaturePadRef = useRef<SignatureCanvas>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    numKids: 0,
    kidsAges: [] as number[],
    signedFor: 'self' as 'self' | 'family' | 'group',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [existingWaiver, setExistingWaiver] = useState<any>(null);
  const [showExisting, setShowExisting] = useState(false);

  // Check for existing waiver when email changes
  useEffect(() => {
    const checkExisting = async () => {
      if (formData.email && formData.email.includes('@')) {
        try {
          const response = await apiService.checkWaiver(formData.email);
          if (response.data.exists) {
            setExistingWaiver(response.data.waiver);
            setShowExisting(true);
          } else {
            setExistingWaiver(null);
            setShowExisting(false);
          }
        } catch (error) {
          // No existing waiver or error - proceed normally
          setExistingWaiver(null);
          setShowExisting(false);
        }
      }
    };

    const debounce = setTimeout(checkExisting, 500);
    return () => clearTimeout(debounce);
  }, [formData.email]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'numKids' ? parseInt(value) || 0 : value,
    }));
  };

  const updateKidsAges = React.useCallback((numKids: number) => {
    const newAges = [...formData.kidsAges];
    if (numKids > newAges.length) {
      // Add more ages
      while (newAges.length < numKids) {
        newAges.push(8);
      }
    } else {
      // Remove excess ages
      newAges.splice(numKids);
    }
    setFormData(prev => ({ ...prev, kidsAges: newAges }));
  }, [formData.kidsAges]);

  useEffect(() => {
    updateKidsAges(formData.numKids);
  }, [formData.numKids, updateKidsAges]);

  const updateKidAge = (index: number, age: number) => {
    const newAges = [...formData.kidsAges];
    newAges[index] = age;
    setFormData(prev => ({ ...prev, kidsAges: newAges }));
  };

  const clearSignature = () => {
    signaturePadRef.current?.clear();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validation
      if (!formData.name || !formData.email) {
        throw new Error('Name and email are required');
      }

      if (!signaturePadRef.current?.isEmpty()) {
        const signatureData = signaturePadRef.current?.toDataURL();
        
        if (signatureData) {
          const waiverData = {
            ...formData,
            signature: signatureData,
          };

          await apiService.createWaiver(waiverData);
        } else {
          throw new Error('Failed to capture signature');
        }
        
        // Navigate to confirmation page with waiver data
        navigate('/confirmation', { 
          state: { 
            waiverData: {
              name: formData.name,
              email: formData.email,
              signedAt: new Date().toISOString(),
            }
          }
        });
      } else {
        throw new Error('Please provide your signature');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to submit waiver');
    } finally {
      setLoading(false);
    }
  };

  if (showExisting && existingWaiver) {
    const isValid = existingWaiver.isValid;
    const expiryDate = new Date(existingWaiver.expiresAt).toLocaleDateString();

    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="text-center">
              <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${
                isValid ? 'bg-green-100' : 'bg-orange-100'
              }`}>
                <Check className={`w-8 h-8 ${isValid ? 'text-green-600' : 'text-orange-600'}`} />
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {isValid ? 'Waiver Already Signed' : 'Waiver Expired'}
              </h2>
              
              <p className="text-gray-600 mb-6">
                {isValid 
                  ? `You have a valid waiver signed on ${new Date(existingWaiver.signedAt).toLocaleDateString()}. It expires on ${expiryDate}.`
                  : `Your waiver expired on ${expiryDate}. Please sign a new waiver.`
                }
              </p>

              {isValid ? (
                <div className="bg-green-50 p-4 rounded-lg mb-6">
                  <p className="text-green-800 font-medium">
                    Show this screen to staff to confirm your valid waiver.
                  </p>
                </div>
              ) : (
                <button
                  onClick={() => setShowExisting(false)}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors mb-6"
                >
                  Sign New Waiver
                </button>
              )}

              <button
                onClick={() => navigate('/')}
                className="text-gray-500 hover:text-gray-700 flex items-center justify-center"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-pink-600/10"></div>
        <div className="relative max-w-4xl mx-auto px-4 py-12">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/')}
              className="flex items-center text-slate-300 hover:text-white mb-6 transition-colors group"
            >
              <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
              Back to Home
            </button>
            <div className="text-center">
              <h1 className="text-4xl font-bold text-white mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Digital Waiver
              </h1>
              <p className="text-slate-300 text-lg">
                Please fill out all required information and sign below to complete your waiver.
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl shadow-2xl p-8 border border-white/20">
            {error && (
              <div className="bg-red-500/10 border border-red-400/30 text-red-300 px-6 py-4 rounded-2xl mb-6 backdrop-blur-sm">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-red-400 rounded-full mr-3"></div>
                  {error}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Personal Information */}
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <h3 className="text-2xl font-semibold text-white mb-6 flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mr-3 text-sm font-bold">1</div>
                  Personal Information
                </h3>
                
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-white/20 rounded-xl text-white placeholder-slate-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all backdrop-blur-sm"
                      placeholder="Enter your full name"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-white/20 rounded-xl text-white placeholder-slate-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all backdrop-blur-sm"
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                </div>

                <div className="mb-0">
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Phone Number (Optional)
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-white/20 rounded-xl text-white placeholder-slate-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all backdrop-blur-sm"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              {/* Group Information */}
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <h3 className="text-2xl font-semibold text-white mb-6 flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mr-3 text-sm font-bold">2</div>
                  Group Information
                </h3>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Signing waiver for:
                  </label>
                  <select
                    name="signedFor"
                    value={formData.signedFor}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all backdrop-blur-sm"
                  >
                    <option value="self" className="bg-slate-800 text-white">Myself only</option>
                    <option value="family" className="bg-slate-800 text-white">My family</option>
                    <option value="group" className="bg-slate-800 text-white">My group</option>
                  </select>
                </div>

                <div className="mb-0">
                  <label className="block text-sm font-medium text-slate-300 mb-3">
                    Number of Kids
                  </label>
                  <div className="flex items-center justify-center space-x-6">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, numKids: Math.max(0, prev.numKids - 1) }))}
                      className="p-3 bg-white/10 border border-white/20 rounded-full hover:bg-white/20 transition-all backdrop-blur-sm text-white"
                    >
                      <Minus className="w-5 h-5" />
                    </button>
                    <span className="text-2xl font-bold w-16 text-center text-white bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">{formData.numKids}</span>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, numKids: prev.numKids + 1 }))}
                      className="p-3 bg-white/10 border border-white/20 rounded-full hover:bg-white/20 transition-all backdrop-blur-sm text-white"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Kids Ages */}
                {formData.numKids > 0 && (
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-slate-300 mb-3">
                      Ages of Kids
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {formData.kidsAges.map((age, index) => (
                        <div key={index}>
                          <input
                            type="number"
                            min="1"
                            max="17"
                            value={age}
                            onChange={(e) => updateKidAge(index, parseInt(e.target.value) || 1)}
                            className="w-full px-4 py-3 border border-white/20 rounded-xl text-white placeholder-slate-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all backdrop-blur-sm text-center"
                            placeholder="Age"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Signature */}
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <h3 className="text-2xl font-semibold text-white mb-6 flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mr-3 text-sm font-bold">3</div>
                  Digital Signature
                </h3>
                
                <div className="bg-white/10 border-2 border-dashed border-white/20 rounded-xl p-6">
                  <p className="text-sm text-slate-300 mb-4 text-center">
                    Please sign in the box below using your mouse, trackpad, or finger (on touch devices)
                  </p>
                  
                  <div className="border border-white/30 rounded-xl overflow-hidden bg-white">
                    <SignatureCanvas
                      ref={signaturePadRef}
                      canvasProps={{
                        className: 'signature-canvas w-full',
                        width: 500,
                        height: 200,
                      }}
                      backgroundColor="white"
                    />
                  </div>
                  
                  <div className="flex justify-center mt-4">
                    <button
                      type="button"
                      onClick={clearSignature}
                      className="flex items-center px-6 py-3 text-sm text-slate-300 bg-white/10 border border-white/20 rounded-xl hover:bg-white/20 transition-all backdrop-blur-sm"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Clear Signature
                    </button>
                  </div>
                </div>
              </div>

              {/* Waiver Text */}
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 mb-8">
                <h4 className="font-semibold text-white mb-4 text-lg">Liability Waiver</h4>
                <div className="text-sm text-slate-300 max-h-40 overflow-y-auto space-y-3 bg-white/5 p-4 rounded-xl">
                  <p>
                    I acknowledge that participation in virtual reality activities involves inherent risks, including but not limited to physical injury, motion sickness, and disorientation.
                  </p>
                  <p>
                    I voluntarily assume all risks and hazards incidental to such participation and hereby waive, release, and discharge Immersa VR Center from any and all liability, claims, demands, actions, or causes of action.
                  </p>
                  <p>
                    This waiver is valid for one year from the date of signing and covers all visits during this period.
                  </p>
                </div>
              </div>

              {/* Submit Button */}
              <div className="text-center">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4 px-8 rounded-2xl font-semibold text-lg hover:from-purple-700 hover:to-pink-700 disabled:from-purple-300 disabled:to-pink-300 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:-translate-y-1 disabled:transform-none"
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    'Sign Waiver & Continue'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};export default WaiverForm;